import { router } from './router.js';
import { store } from './store.js';
import { api } from './api.js';
import { eventBus } from './events.js';
import { AppLayout } from './components/layout/layout.js';
import { registerAuthRoutes } from './modules/auth/index.js';
import { hasAnyPermission, hasPlanAnyFeature, isOwner } from './modules/auth/auth-service.js';
import { Toast } from './components/ui/toast.js';
import { initDb } from './db.js';
import { syncManager } from './sync.js';
import { notifications } from './notifications.js';
import { startCallListener, stopCallListener } from './modules/integrations/call-popup.js';
import { startUpdateChecker } from './update-checker.js';
import { loadBranding } from './core/branding.js';
import { loadClientProperties } from './core/client-properties.js';
import { CapacitorBridge } from './core/capacitor-bridge.js';

const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/accept-invite', '/verify-email'];

function isAuthPage(path) {
    return AUTH_PATHS.some(p => path === p || path.startsWith(p + '?'));
}

let layout = null;

function initLayout() {
    layout = new AppLayout();
    layout.mount('#app');

    const content = layout.getContentEl();
    if (content) {
        router.start(content);
    }
}

function registerRoutes() {
    const authMiddleware = (ctx) => {
        if (!isAuthPage(ctx.path) && !api.getToken()) {
            router.navigate('/login');
            return false;
        }
    };

    const layoutMiddleware = (ctx) => {
        const wasShell = layout && layout._renderedAsShell;
        const isPublic = isAuthPage(ctx.path);

        layout._forcePublic = isPublic;

        const nowShell = layout.showAppShell;

        // Re-render layout when shell state changes
        if (wasShell !== nowShell) {
            layout.render();
            const content = layout.getContentEl();
            if (content) {
                router._container = content;
            }
        }
    };

    // Route → required permissions (OR-logic: any one grants access)
    const ROUTE_PERMISSIONS = {
        '/pulse':                 ['dashboard.view'],
        '/finance':               ['finance.view', 'finance.manage'],
        '/finance/transactions':  ['finance.view', 'finance.manage'],
        '/finance/accounts':      ['finance.manage'],
        '/finance/reports':       ['finance.reports', 'finance.view'],
        '/finance/counterparties':['finance.view', 'finance.manage'],
        '/finance/shifts':        ['finance.view', 'finance.manage'],
        '/finance/shift/:id':     ['finance.view', 'finance.manage'],
        '/finance/monthly-expenses': ['finance.manage'],
        '/salary':                ['salary.view', 'salary.view_own', 'salary.manage'],
        '/salary/rules':          ['salary.manage'],
        '/warehouse':             ['warehouse.view', 'warehouse.manage'],
        '/warehouse/products':    ['warehouse.view', 'warehouse.manage'],
        '/warehouse/movements':   ['warehouse.view', 'warehouse.manage'],
        '/warehouse/sales':       ['warehouse.view', 'warehouse.manage'],
        '/warehouse/incomings':   ['warehouse.view', 'warehouse.manage'],
        '/warehouse/writeoffs':   ['warehouse.view', 'warehouse.manage'],
        '/warehouse/inventories': ['warehouse.view', 'warehouse.manage'],
        '/warehouse/incoming':    ['warehouse.manage'],
        '/warehouse/writeoff':    ['warehouse.manage'],
        '/warehouse/inventory':   ['warehouse.manage'],
        '/warehouse/sale':        ['warehouse.manage'],
        '/warehouse/transfer':    ['warehouse.manage'],
        '/warehouse/orders':      ['warehouse.manage'],
        '/warehouse/documents':   ['warehouse.view', 'warehouse.manage'],
        '/warehouse/documents/new': ['warehouse.manage'],
        '/warehouse/documents/edit': ['warehouse.manage'],
        '/warehouse/documents/print': ['warehouse.view', 'warehouse.manage'],
        '/warehouse/counterparties': ['warehouse.manage'],
        '/warehouse/counterparties/detail': ['warehouse.manage'],
        '/warehouse/legal-entities': ['warehouse.manage'],
        '/warehouse/batches':     ['warehouse.view', 'warehouse.manage'],
        '/warehouse/pricelists':  ['warehouse.view', 'warehouse.manage'],
        '/warehouse/pricelists/edit': ['warehouse.manage'],
        '/warehouse/tech-cards':  ['warehouse.manage'],
        '/warehouse/tech-cards/new': ['warehouse.manage'],
        '/warehouse/tech-cards/edit': ['warehouse.manage'],
        '/warehouse/production':  ['warehouse.manage'],
        '/warehouse/production/detail': ['warehouse.manage'],
        '/warehouse/analytics':   ['warehouse.view', 'warehouse.manage'],
        '/warehouse/scanner':     ['warehouse.view', 'warehouse.manage'],
        '/warehouse/auto-orders': ['warehouse.manage'],
        '/warehouse/integrations': ['warehouse.manage'],
        '/analytics':             ['analytics.view'],
        '/promotions':            ['promotions.view', 'promotions.manage'],
        '/online-booking':        ['online_booking.manage'],
        '/integrations':          ['integrations.view', 'integrations.manage'],
        '/integrations/megafon':  ['integrations.manage'],
        '/integrations/telegram': ['integrations.manage'],
        '/integrations/max':      ['integrations.manage'],
        '/integrations/yandex-booking': ['integrations.manage'],
        '/integrations/instagram': ['integrations.manage'],
        '/staff':                 ['staff.view', 'staff.manage'],
        '/resources':             ['resource.view', 'resource.manage'],
        '/calls':                 ['calls.view', 'integrations.view'],
        '/integrations/calls':    ['calls.view', 'integrations.view'],
        '/reports':               ['reports.view', 'settings.salon'],
        '/reviews':               ['settings.salon'],
        '/reports/daily':         ['analytics.view'],
        '/notifications':         ['notifications.manage'],
    };

    const permissionMiddleware = (ctx) => {
        if (isAuthPage(ctx.path)) return;

        // Find matching permission rule (exact match or prefix)
        const perms = ROUTE_PERMISSIONS[ctx.path];
        if (!perms || perms.length === 0) return; // no restriction

        if (!hasAnyPermission(...perms)) {
            Toast.error('Нет доступа к этому разделу');
            window.location.hash = '#/';
            return false;
        }
    };

    // Route → required plan features (OR-logic: any one grants access)
    const ROUTE_PLAN_FEATURES = {
        '/finance':               ['finance', 'finance_basic'],
        '/finance/transactions':  ['finance', 'finance_basic'],
        '/finance/accounts':      ['finance'],
        '/finance/reports':       ['finance'],
        '/finance/counterparties':['finance'],
        '/finance/shifts':        ['finance', 'finance_basic'],
        '/finance/shift/:id':     ['finance', 'finance_basic'],
        '/finance/monthly-expenses': ['finance'],
        '/salary':                ['salary'],
        '/salary/rules':          ['salary'],
        '/warehouse':             ['warehouse'],
        '/warehouse/products':    ['warehouse'],
        '/warehouse/movements':   ['warehouse'],
        '/warehouse/sales':       ['warehouse'],
        '/warehouse/incomings':   ['warehouse'],
        '/warehouse/writeoffs':   ['warehouse'],
        '/warehouse/inventories': ['warehouse'],
        '/warehouse/incoming':    ['warehouse'],
        '/warehouse/writeoff':    ['warehouse'],
        '/warehouse/inventory':   ['warehouse'],
        '/warehouse/sale':        ['warehouse'],
        '/warehouse/transfer':    ['warehouse'],
        '/loyalty':               ['loyalty'],
        '/loyalty/cards':         ['loyalty'],
        '/resources':             ['resource_booking'],
        '/groups':                ['group_classes'],
        '/group-schedule':        ['group_classes'],
        '/class-subscriptions':         ['subscriptions'],
        '/loyalty/certificates':  ['loyalty'],
        '/loyalty/subscriptions': ['subscriptions'],
        '/analytics':             ['analytics', 'analytics_basic'],
        '/promotions':            ['promotions'],
        '/online-booking':        ['online_booking', 'online_booking_basic'],
        '/integrations':          ['integrations'],
        '/calls':                 ['integrations'],
        '/integrations/calls':    ['integrations'],
        '/reports':               ['reports'],
        '/reviews':               ['reviews'],
        '/crm':                   ['crm'],
        '/reports/daily':         ['analytics', 'analytics_basic'],
        '/warehouse/orders':      ['warehouse'],
        '/warehouse/documents':   ['warehouse'],
        '/warehouse/documents/new': ['warehouse'],
        '/warehouse/documents/edit': ['warehouse'],
        '/warehouse/documents/print': ['warehouse'],
        '/warehouse/counterparties': ['warehouse'],
        '/warehouse/counterparties/detail': ['warehouse'],
        '/warehouse/legal-entities': ['warehouse'],
        '/warehouse/batches':     ['warehouse'],
        '/warehouse/pricelists':  ['warehouse'],
        '/warehouse/pricelists/edit': ['warehouse'],
        '/warehouse/tech-cards':  ['warehouse'],
        '/warehouse/tech-cards/new': ['warehouse'],
        '/warehouse/tech-cards/edit': ['warehouse'],
        '/warehouse/production':  ['warehouse'],
        '/warehouse/production/detail': ['warehouse'],
        '/warehouse/analytics':   ['warehouse'],
        '/warehouse/scanner':     ['warehouse'],
        '/warehouse/auto-orders': ['warehouse'],
        '/warehouse/integrations': ['warehouse'],
    };

    // Map route paths to feature keys for upgrade prompt
    const ROUTE_FEATURE_KEY = {
        '/finance': 'finance', '/salary': 'salary', '/warehouse': 'warehouse',
        '/loyalty': 'loyalty', '/analytics': 'analytics', '/promotions': 'promotions',
        '/online-booking': 'online-booking', '/integrations': 'integrations',
        '/calls': 'integrations',
        '/reports': 'reports',
        '/reviews': 'reviews',
        '/crm': 'crm',
    };

    const planMiddleware = (ctx) => {
        if (isAuthPage(ctx.path)) return;

        const features = ROUTE_PLAN_FEATURES[ctx.path];
        if (!features || features.length === 0) return;

        if (!hasPlanAnyFeature(...features)) {
            console.warn('[PlanGate] Blocked:', ctx.path, 'requires:', features, 'plan:', JSON.stringify(store.plan?.features));
            // Find the base path for upgrade prompt feature key
            const basePath = '/' + ctx.path.split('/')[1];
            const featureKey = ROUTE_FEATURE_KEY[basePath] || ROUTE_FEATURE_KEY[ctx.path] || 'finance';
            import('./components/ui/upgrade-prompt.js').then(m => m.showUpgradePrompt(featureKey));
            window.location.hash = '#/';
            return false;
        }
    };

    router.use(authMiddleware);
    router.use(layoutMiddleware);
    router.use(planMiddleware);
    router.use(permissionMiddleware);

    registerAuthRoutes();

    router.add('/', () => import('./modules/dashboard/index.js'));
    router.add('/pulse', () => import('./modules/pulse/index.js'));
    router.add('/calendar', () => import('./modules/calendar/index.js'));
    router.add('/clients', () => import('./modules/clients/index.js'));
    router.add('/clients/:id', () => import('./modules/clients/show.js'));
    router.add('/visits', () => import('./modules/visits/index.js'));
    router.add('/visits/:id', () => import('./modules/visits/show.js'));
    router.add('/staff', () => import('./modules/staff/index.js'));
    router.add('/staff/:id', () => import('./modules/staff/show.js'));
    router.add('/services', () => import('./modules/services/index.js'));
    router.add('/resources', () => import('./modules/resources/index.js'));
    router.add('/finance', () => import('./modules/finance/index.js'));
    router.add('/finance/transactions', () => import('./modules/finance/transactions-page.js'));
    router.add('/finance/accounts', () => import('./modules/finance/accounts-page.js'));
    router.add('/finance/reports', () => import('./modules/finance/reports-page.js'));
    router.add('/finance/counterparties', () => import('./modules/finance/counterparties-page.js'));
    router.add('/finance/shifts', () => import('./modules/finance/shifts-page.js'));
    router.add('/finance/shift/:id', () => import('./modules/finance/shift-detail-page.js'));
    router.add('/finance/monthly-expenses', () => import('./modules/finance/monthly-expenses-page.js'));
    router.add('/salary', () => import('./modules/salary/index.js'));
    router.add('/salary/rules', () => import('./modules/salary/rules-page.js'));
    // Warehouse shell (persistent tabs, no re-render on tab switch)
    const whShell = () => import('./modules/warehouse/shell-entry.js');
    router.add('/warehouse', whShell);
    router.add('/warehouse/products', whShell);
    router.add('/warehouse/movements', whShell);
    router.add('/warehouse/sales', whShell);
    router.add('/warehouse/incomings', whShell);
    router.add('/warehouse/writeoffs', whShell);
    router.add('/warehouse/inventories', whShell);
    router.add('/warehouse/orders', whShell);
    router.add('/warehouse/documents', whShell);
    router.add('/warehouse/documents/new', whShell);
    router.add('/warehouse/documents/edit', whShell);
    router.add('/warehouse/documents/print', whShell);
    router.add('/warehouse/counterparties', whShell);
    router.add('/warehouse/counterparties/detail', whShell);
    router.add('/warehouse/legal-entities', whShell);
    router.add('/warehouse/batches', whShell);
    router.add('/warehouse/pricelists', whShell);
    router.add('/warehouse/pricelists/edit', whShell);
    router.add('/warehouse/tech-cards', whShell);
    router.add('/warehouse/tech-cards/new', whShell);
    router.add('/warehouse/tech-cards/edit', whShell);
    router.add('/warehouse/production', whShell);
    router.add('/warehouse/production/detail', whShell);
    router.add('/warehouse/analytics', whShell);
    router.add('/warehouse/scanner', whShell);
    router.add('/warehouse/auto-orders', whShell);
    router.add('/warehouse/integrations', whShell);
    // Warehouse form pages (separate routes)
    router.add('/warehouse/products/:id', () => import('./modules/warehouse/product-show.js'));
    router.add('/warehouse/incoming', () => import('./modules/warehouse/incoming-page.js'));
    router.add('/warehouse/writeoff', () => import('./modules/warehouse/writeoff-entry.js'));
    router.add('/warehouse/inventory', () => import('./modules/warehouse/inventory-page.js'));
    router.add('/warehouse/sale', () => import('./modules/warehouse/sale-entry.js'));
    router.add('/warehouse/transfer', () => import('./modules/warehouse/transfer-entry.js'));
    // Groups (Промпт 5: sport+education group classes + subscriptions)
    router.add('/groups', () => import('./modules/groups/index.js'));
    router.add('/group-schedule', () => import('./modules/groups/index.js').then(m => ({
        default: m.mountSchedule, cleanup: m.cleanup,
    })));
    router.add('/class-subscriptions', () => import('./modules/groups/index.js').then(m => ({
        default: m.mountSubscriptions, cleanup: m.cleanup,
    })));
    router.add('/loyalty', () => import('./modules/loyalty/index.js'));
    router.add('/loyalty/cards', () => import('./modules/loyalty/cards-entry.js'));
    router.add('/promotions', () => import('./modules/promotions/index.js'));
    router.add('/loyalty/certificates', () => import('./modules/loyalty/certificates-entry.js'));
    router.add('/loyalty/subscriptions', () => import('./modules/loyalty/subscriptions-entry.js'));
    router.add('/analytics', () => import('./modules/analytics/index.js'));
    router.add('/online-booking', () => import('./modules/online-booking/index.js'));
    router.add('/integrations', () => import('./modules/integrations/index.js'));
    router.add('/integrations/megafon', () => import('./modules/integrations/megafon-entry.js'));
    router.add('/integrations/telegram', () => import('./modules/integrations/telegram-entry.js'));
    router.add('/integrations/max', () => import('./modules/integrations/max-entry.js'));
    router.add('/integrations/yandex-booking', () => import('./modules/integrations/yandex-booking-entry.js'));
    router.add('/integrations/instagram', () => import('./modules/integrations/instagram-entry.js'));
    router.add('/integrations/calls', () => import('./modules/integrations/calls-entry.js'));
    router.add('/integrations/contacts-sync', () => import('./modules/integrations/contacts-sync-entry.js'));
    router.add('/calls', () => import('./modules/integrations/calls-entry.js'));
    router.add('/crm', () => import('./modules/crm/index.js'));
    router.add('/personal-contacts', () => {
        // Redirect to CRM
        window.location.hash = '#/crm?tab=contacts';
        return import('./modules/crm/index.js');
    });
    router.add('/reports', () => import('./modules/reports/index.js'));
    router.add('/reviews', () => import('./modules/reviews/index.js'));
    router.add('/reports/daily', () => import('./modules/reports/daily-report-page.js').then(m => ({ default: (c, ctx) => { const p = new m.DailyReportPage({ query: ctx?.query }); p.mount(c); }, cleanup: () => {} })));
    router.add('/billing', () => import('./modules/billing/index.js'));
    router.add('/support', () => import('./modules/support/index.js'));
    router.add('/notifications', () => ({ default: () => { window.location.hash = '#/settings?tab=notifications'; }, cleanup: () => {} }));
    router.add('/settings', () => import('./modules/settings/index.js'));
}

function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) return;
    const swUrl = window.__SW_URL || '/sw.js';

    // Unregister old SW at different URL (e.g. /sw.js → /sw-{hash}.js migration)
    navigator.serviceWorker.getRegistrations().then(regs => {
        for (const reg of regs) {
            if (reg.active && !reg.active.scriptURL.includes(new URL(swUrl, location.origin).pathname)) {
                reg.unregister();
            }
        }
    }).catch(() => {});

    navigator.serviceWorker.register(swUrl, { updateViaCache: 'none' })
        .then(reg => { if (reg) reg.update().catch(() => {}); })
        .catch(() => {});
}

function hidePreloader() {
    const preloader = document.getElementById('preloader');
    if (preloader) {
        preloader.classList.add('fade-out');
        setTimeout(() => preloader.remove(), 300);
    }
}

function showMaintenanceScreen() {
    hidePreloader();
    const el = document.getElementById('maintenance');
    if (el) {
        el.classList.add('visible');
    }
    const app = document.getElementById('app');
    if (app) {
        app.style.display = 'none';
    }
}

async function checkServiceAvailability() {
    try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        const res = await fetch('/api/v1/health', { signal: controller.signal });
        clearTimeout(timeout);
        if (res.status === 503) {
            return false;
        }
        const json = await res.json();
        if (json.status === 'degraded' && json.checks?.database === 'unavailable') {
            return false;
        }
        return true;
    } catch {
        return true; // allow app to load even if health check fails
    }
}

async function initOffline() {
    try {
        await initDb();
    } catch {
        // IndexedDB not available
    }

    syncManager.start();
    notifications.initServiceWorkerListener();
}

eventBus.on('auth:logout', () => {
    store.user = null;
    store.account = null;
    store.branch = null;
    store.plan = null;
    api.clearTokens();
    stopCallListener();

    // Force layout to re-render as public before navigating
    // (tokens already cleared so layoutMiddleware can't detect the shell→public transition)
    if (layout) {
        layout._forcePublic = true;
        layout.render();
        const content = layout.getContentEl();
        if (content) {
            router._container = content;
        }
    }

    router.navigate('/login');
});

eventBus.on('plan:upgrade_required', ({ message }) => {
    Toast.error(message || 'Функция недоступна на вашем тарифе');
});

eventBus.on('auth:login', async () => {
    layout.render();
    const content = layout.getContentEl();
    if (content) {
        router._container = content;
    }
    if (hasPlanAnyFeature('integrations')) {
        startCallListener();
    }
    loadClientProperties().catch(() => {});
    router.navigate('/');

    // Show onboarding wizard for new owners (or if manually requested via #/wizard)
    const wantWizard = location.hash === '#/wizard';
    if (wantWizard || (isOwner() && !store.account?.settings?.onboarding?.completed)) {
        try {
            const { SetupWizard } = await import('./modules/onboarding/setup-wizard.js');
            if (wantWizard) {
                // Reset completed flag so wizard shows properly
                await api.put('/account/onboarding', { completed: false, step: 1 }).catch(() => {});
            }
            new SetupWizard().open();
            if (wantWizard) location.hash = '#/';
        } catch {
            // Onboarding not critical
        }
    }
});

const _d = typeof window.dbg === 'function' ? window.dbg : () => {};

async function handleImpersonateToken() {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return false;

    const params = new URLSearchParams(hash.slice(qIdx));
    const token = params.get('impersonate_token');
    if (!token) return false;

    // Clear old session state
    store.user = null;
    store.account = null;
    store.branch = null;
    store.plan = null;
    api.clearTokens();

    // Set impersonate token (access only, no refresh)
    api.setTokens(token, null);

    // Clean URL
    params.delete('impersonate_token');
    const cleanHash = hash.slice(0, qIdx) + (params.toString() ? '?' + params.toString() : '');
    history.replaceState(null, '', cleanHash || '#/');

    // Load user profile for the impersonated session
    try {
        const { authService } = await import('./modules/auth/auth-service.js');
        await authService._loadUser();
    } catch {
        // If loading fails, token may be invalid
        api.clearTokens();
        return false;
    }

    return true;
}

async function init() {
    try {
        const impersonating = await handleImpersonateToken();

        const available = await checkServiceAvailability();
        _d('health check: ' + (available ? 'ok' : 'unavailable'));
        if (!available) {
            showMaintenanceScreen();
            return;
        }

        await loadBranding();

        // Refresh plan data from server before routing (localStorage may be stale)
        if (api.getToken() && !impersonating) {
            try {
                const { authService } = await import('./modules/auth/auth-service.js');
                await authService._loadUser();
                _d('user & plan refreshed');
            } catch {
                // stale token — auth middleware will handle redirect
            }
        }

        registerRoutes();
        _d('routes registered');
        initLayout();
        _d('layout init done');
        CapacitorBridge.init(router);
        registerServiceWorker();
        startUpdateChecker();
        initOffline();
        hidePreloader();
        _d('app ready');

        if (impersonating) {
            _d('impersonating via admin token');
        }

        if (api.getToken()) {
            loadClientProperties().catch(() => {});
            if (hasPlanAnyFeature('integrations')) {
                startCallListener();
            }
        }
    } catch (err) {
        _d('INIT ERROR: ' + err.message);
        console.error('[BWClients] Init failed:', err);
        showMaintenanceScreen();
    }
}

// Global API error handling — show user-friendly toasts
let _lastNetworkToast = 0;
eventBus.on('api:error', ({ message }) => {
    const now = Date.now();
    if (message === 'network' && now - _lastNetworkToast > 5000) {
        _lastNetworkToast = now;
        Toast.error('Нет подключения к серверу. Проверьте интернет.');
    } else if (message === 'timeout') {
        Toast.error('Сервер не отвечает. Попробуйте позже.');
    } else if (message === '503') {
        Toast.error('Сервис временно недоступен. Попробуйте позже.');
    }
});
eventBus.on('auth:session_expired', () => {
    Toast.error('Сессия истекла. Войдите заново.');
});

// Global error reporting
(function setupErrorReporting() {
    const reported = new Set();
    function report(type, message, file, line) {
        const key = `${message}:${file}:${line}`;
        if (reported.has(key)) return;
        reported.add(key);
        setTimeout(() => reported.delete(key), 300000); // dedupe 5 min
        try {
            const body = JSON.stringify({ type, message: String(message).slice(0, 500), file, line, url: location.hash, ua: navigator.userAgent.slice(0, 120), v: document.querySelector('meta[name="version"]')?.content || '' });
            if (navigator.sendBeacon) {
                navigator.sendBeacon('/api/v1/health/client-error', new Blob([body], { type: 'application/json' }));
            }
        } catch {}
    }
    window.addEventListener('error', (e) => {
        if (e.filename?.includes('extension') || e.filename?.includes('chrome-extension')) return;
        report('error', e.message, e.filename, e.lineno);
    });
    window.addEventListener('unhandledrejection', (e) => {
        const msg = e.reason?.message || e.reason?.toString?.() || 'Unhandled rejection';
        report('rejection', msg, '', 0);
    });
})();

init();
